import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Navigation from './Navigation.js';
import logo from '../assets/img/logo.png';
import avatar from '../assets/img/user_avatar.jpeg';
import troiano_logo from '../assets/img/Troiano_Logo.jpg';
import clsx from 'clsx';
import { CONFIG } from "../config";

const Header = () => {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fullName, setFullName] = useState("User");
  const [organisationName, setOrganisationName] = useState("");
  const [defaultVehicleId, setDefaultVehicleId] = useState(0);

  const restrictedIds = JSON.parse(process.env.REACT_APP_RESTRICTED_USER_ID || '[]');
  const userId = parseInt(localStorage.getItem("tensor-user-id"), 10);
  const isRestricted = restrictedIds.includes(userId);

  const navClasses = clsx(
    'items-center space-x-6',
    mobileMenuOpen
      ? 'absolute top-full left-0 right-0 bg-white px-4 py-4 flex flex-col space-y-4 transition-all duration-300'
      : 'hidden md:flex',
    'md:flex md:flex-row md:static md:bg-transparent md:px-0 md:py-0'
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('tensor-auth-token');
    setIsLoggedIn(!!token);

    const firstName = localStorage.getItem("tensor-user-first-name") || "NA";
    const lastName = localStorage.getItem("tensor-user-last-name") || "";
    const organisation = localStorage.getItem("tensor-user-organisation-name") || "";
    const defaultVehicleId = localStorage.getItem("tensor-default-vehicle-id") || "0";
    setFullName(`${firstName} ${lastName}`);
    setOrganisationName(`${organisation}`);
    setDefaultVehicleId(`${defaultVehicleId}`);
  }, []);

  const logoutUser = async () => {
    setLoading(true);
    try {
      const response = await fetch(CONFIG.API_BASE_URL + "/" + CONFIG.LOGOUT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('tensor-auth-token')}`,
        },
      });

      if (response.ok) {
        localStorage.removeItem('tensor-auth-token');
        localStorage.removeItem('tensor-user-id');
        localStorage.removeItem('tensor-user-first-name');
        localStorage.removeItem('tensor-user-last-name');
        localStorage.removeItem('tensor-user-organisation-name');
        localStorage.removeItem('tensor-default-vehicle-id');
        navigate('/', { state: { message: 'Logged out successfully.', type: 'success' } });
      }
    } catch (error) {
      console.error('An error occurred during logout:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <header className="bg-white shadow">
      <div className="container mx-auto px-1 py-4 flex items-center justify-between relative">
        {/* Logo */}
        <div className="flex items-center space-x-2">
          <Link to={`/dashboard`} className="text-blue-600 hover:underline cursor-pointer">
            <img src={logo} alt="Tensor Planet Logo" className="h-8 w-auto" />
          </Link>
        </div>

        {isLoggedIn && (
          <>
            {/* Mobile Menu Toggle */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-600 hover:text-gray-900 focus:outline-none"
              >
                {mobileMenuOpen ? (
                  <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6 18L18 6M6 6l12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M4 6h16M4 12h16M4 18h16"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
              </button>
            </div>

            {/* Navigation */}
            <Navigation defaultVehicleId={defaultVehicleId} navClasses={navClasses} />

            {/* User Profile */}
            <div className="relative flex items-center space-x-2 cursor-pointer" ref={dropdownRef} onClick={() => setDropdownOpen((prev) => !prev)}>
              <span className="head_organisation_text">{organisationName}</span>
              <img
                src={isRestricted ? troiano_logo : avatar}
                alt={isRestricted ? "Troiano Logo" : "User Avatar"}
                className="h-12 w-12 rounded-full object-cover"
                title={isRestricted ? "Troiano" : ""}
              />
              <span className="head_username_text">{fullName}</span>
              <span className="text-gray-600">&#9662;</span>

              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-44 bg-white border border-gray-200 rounded shadow-lg z-10">
                  <ul>
                    <li>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          logoutUser();
                        }}
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                      >
                        Logout
                      </a>
                      {loading && (
                        <div className="fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
                          <div className="text-gray-700 text-lg font-semibold">Logging out...</div>
                        </div>
                      )}
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
